(() => {
if (window.restoreStickyCartReady) return;
window.restoreStickyCartReady = true;

class RestoreStickyCart {
  constructor(root) {
    this.root = root;
    this.plan = root.querySelector('[data-restore-sticky-plan]');
    this.price = root.querySelector('[data-restore-sticky-price]');
    this.compare = root.querySelector('[data-restore-sticky-compare]');
    this.submit = root.querySelector('[data-restore-sticky-submit]');
    this.variant = root.querySelector('[data-restore-sticky-variant]');
    this.events = new AbortController();
    this.observers = [];
    this.bindEvents();
    this.observeChat();
    this.syncFromMain();
    this.updateVisibility();
    root.hidden = false;
  }

  mainProduct() {
    return document.querySelector(`product-info[data-product-id="${this.root.dataset.productId}"]`);
  }

  mainButton() {
    return this.mainProduct()?.querySelector('.product-form__submit[name="add"]');
  }

  purchaseCard() {
    return this.mainProduct()?.querySelector('.hgl-subscription-card');
  }

  bindEvents() {
    const signal = this.events.signal;
    this.plan?.addEventListener('change', () => this.syncToMain(), { signal });
    this.submit?.addEventListener('click', () => this.addToCart(), { signal });
    document.addEventListener('restore:purchase-option-change', () => this.syncFromMain(), { signal });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncFromMain());
    }
    document.addEventListener('change', (event) => {
      if (!this.root.contains(event.target)) this.syncFromMain();
    }, { signal });
    window.addEventListener('scroll', () => this.queueVisibilityUpdate(), { passive: true, signal });
    window.addEventListener('resize', () => this.queueVisibilityUpdate(), { signal });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(this.root)) this.destroy();
    }, { signal });
  }

  syncVariant() {
    const mainVariant = this.mainProduct()?.querySelector('input[name="id"]');
    if (mainVariant && this.variant) this.variant.value = mainVariant.value;
  }

  availablePlans(card) {
    const mainSelect = card?.querySelector('[data-selling-plan-select]');
    if (mainSelect) return Array.from(mainSelect.options).map(({ value, text }) => ({ value, text }));
    const input = card?.querySelector('[data-selling-plan-input]');
    const label = card?.querySelector('.hgl-subscription-card__plan-label')?.textContent.trim();
    const planId = input?.value || input?.dataset.defaultSellingPlan;
    return planId ? [{ value: planId, text: label || 'Subscribe and save' }] : [];
  }

  rebuildPlans(card, selectedValue) {
    if (!this.plan) return;
    const plans = this.availablePlans(card);
    this.plan.replaceChildren(...plans.map(({ value, text }) => new Option(text, value)));
    this.plan.add(new Option('One Time Purchase', ''));
    this.plan.value = plans.some(({ value }) => value === selectedValue) ? selectedValue : '';
    this.root.querySelector('.restore-sticky__form')?.classList.toggle('restore-sticky__form--one-time', !plans.length);
  }

  syncPrice(card, isOneTime) {
    const selector = `[data-purchase-option="${isOneTime ? 'one-time' : 'subscription'}"]`;
    const priceRoot = card?.querySelector(selector)?.querySelector('.hgl-subscription-card__price');
    const compare = priceRoot?.querySelector('.hgl-subscription-card__compare');
    const priceText = Array.from(priceRoot?.childNodes || []).find((node) => node.nodeType === Node.TEXT_NODE);
    if (this.price) this.price.textContent = priceText?.textContent.trim() || priceRoot?.textContent.trim() || '';
    if (!this.compare) return;
    this.compare.textContent = compare?.textContent.trim() || '';
    this.compare.hidden = !this.compare.textContent;
  }

  syncFromMain() {
    const card = this.purchaseCard();
    const active = card?.querySelector('.hgl-subscription-card__option--active');
    const isOneTime = active?.dataset.purchaseOption === 'one-time';
    const selectedPlan = isOneTime ? '' : card?.querySelector('[data-selling-plan-input]')?.value || '';
    this.rebuildPlans(card, selectedPlan);
    this.syncPrice(card, isOneTime);
    this.syncVariant();
    const mainButton = this.mainButton();
    if (!mainButton || !this.submit) return;
    this.submit.disabled = mainButton.disabled;
    this.submit.querySelector('span').textContent = mainButton.querySelector('span')?.textContent.trim() || '';
  }

  syncToMain() {
    const card = this.purchaseCard();
    if (!card || !this.plan) return;
    const isOneTime = this.plan.value === '';
    const mainSelect = card.querySelector('[data-selling-plan-select]');
    if (!isOneTime && mainSelect && mainSelect.value !== this.plan.value) {
      mainSelect.value = this.plan.value;
      mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    card.querySelector(`[data-purchase-option="${isOneTime ? 'one-time' : 'subscription'}"]`)?.click();
    this.syncPrice(card, isOneTime);
  }

  addToCart() {
    this.syncToMain();
    this.syncVariant();
    const mainButton = this.mainButton();
    if (mainButton && !mainButton.disabled) mainButton.click();
  }

  queueVisibilityUpdate() {
    cancelAnimationFrame(this.visibilityFrame);
    this.visibilityFrame = requestAnimationFrame(() => this.updateVisibility());
  }

  updateVisibility() {
    const visible = Boolean(this.mainButton()?.getBoundingClientRect().bottom <= 0);
    this.root.classList.toggle('is-visible', visible);
    this.root.setAttribute('aria-hidden', String(!visible));
    this.syncChatOffset(visible);
  }

  syncChatOffset(visible) {
    const offset = visible ? this.root.offsetHeight + 20 : 20;
    document.querySelector('shopify-chat')?.style.setProperty('--shopify-agent-activator-offset', `${offset}px`, 'important');
    const chat = document.querySelector('#ShopifyChat');
    if (!chat) return;
    if (visible) chat.style.setProperty('bottom', `${offset}px`, 'important');
    else chat.style.removeProperty('bottom');
  }

  observeChat() {
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.syncChatOffset(this.root.classList.contains('is-visible')));
      observer.observe(this.root);
      this.observers.push(observer);
    }
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => this.syncChatOffset(this.root.classList.contains('is-visible')));
    observer.observe(document.body, { childList: true, subtree: true });
    this.observers.push(observer);
  }

  destroy() {
    this.events.abort();
    this.observers.forEach((observer) => observer.disconnect());
    if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
    cancelAnimationFrame(this.visibilityFrame);
    this.syncChatOffset(false);
  }
}

const initRestoreStickyCart = (container = document) => {
  container.querySelectorAll('[data-restore-sticky]').forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    new RestoreStickyCart(root);
  });
};

initRestoreStickyCart();
document.addEventListener('shopify:section:load', (event) => initRestoreStickyCart(event.target));
})();
